import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Activity, ChevronDown, ChevronRight, CheckCircle, Circle, Clock, FilePlus, ExternalLink, X, Search, FileText } from 'lucide-react'

const CLAUSES = [
  {
    id: '4', title: 'Context of the Organization',
    items: [
      { id: '4.1', text: 'Understanding the organization and its context — internal & external issues identified' },
      { id: '4.2', text: 'Understanding the needs and expectations of interested parties' },
      { id: '4.3', text: 'Determining the scope of the ISMS' },
      { id: '4.4', text: 'Information security management system established, implemented, maintained and continually improved' },
    ]
  },
  {
    id: '5', title: 'Leadership',
    items: [
      { id: '5.1', text: 'Leadership and commitment — top management demonstrates leadership' },
      { id: '5.2', text: 'Information security policy established, documented and communicated' },
      { id: '5.3', text: 'Organizational roles, responsibilities and authorities assigned and communicated' },
    ]
  },
  {
    id: '6', title: 'Planning',
    items: [
      { id: '6.1.1', text: 'Actions to address risks and opportunities — general' },
      { id: '6.1.2', text: 'Information security risk assessment process defined and performed' },
      { id: '6.1.3', text: 'Information security risk treatment — SoA produced' },
      { id: '6.2', text: 'Information security objectives established and plans to achieve them' },
      { id: '6.3', text: 'Planning of changes — changes to ISMS carried out in a planned manner' },
    ]
  },
  {
    id: '7', title: 'Support',
    items: [
      { id: '7.1', text: 'Resources determined and provided for the ISMS' },
      { id: '7.2', text: 'Competence of persons determined and maintained' },
      { id: '7.3', text: 'Awareness of information security policy, ISMS objectives and their contribution' },
      { id: '7.4', text: 'Communication — internal and external communications determined' },
      { id: '7.5', text: 'Documented information — created, updated and controlled' },
    ]
  },
  {
    id: '8', title: 'Operation',
    items: [
      { id: '8.1', text: 'Operational planning and control — processes planned and implemented' },
      { id: '8.2', text: 'Information security risk assessment performed at planned intervals' },
      { id: '8.3', text: 'Information security risk treatment plan implemented' },
    ]
  },
  {
    id: '9', title: 'Performance Evaluation',
    items: [
      { id: '9.1', text: 'Monitoring, measurement, analysis and evaluation of ISMS performance' },
      { id: '9.2', text: 'Internal audit programme established and internal audits performed' },
      { id: '9.3', text: 'Management review conducted at planned intervals' },
    ]
  },
  {
    id: '10', title: 'Improvement',
    items: [
      { id: '10.1', text: 'Continual improvement of the ISMS' },
      { id: '10.2', text: 'Nonconformities and corrective actions addressed and documented' },
    ]
  },
]

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Compliant', 'Partially Compliant', 'Not Applicable']

const statusIcon = (s) => {
  if (s === 'Compliant') return <CheckCircle size={16} color="var(--accent)" />
  if (s === 'In Progress') return <Clock size={16} color="var(--warning)" />
  if (s === 'Partially Compliant') return <Clock size={16} color="var(--warning)" />
  return <Circle size={16} color="var(--text-muted)" />
}

const statusBadge = (s) => ({
  'Compliant': 'badge-success',
  'In Progress': 'badge-warning',
  'Partially Compliant': 'badge-warning',
  'Not Started': 'badge-danger',
  'Not Applicable': 'badge-neutral',
}[s] || 'badge-neutral')

function DocPickerModal({ isOpen, onClose, onSelect, selectedIds = [], documents = [] }) {
  const [search, setSearch] = useState('')
  if (!isOpen) return null

  const filtered = documents.filter(d => d.title.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2>Attach Evidence</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input 
              className="form-input" 
              style={{ paddingLeft: 34, fontSize: '0.8125rem' }} 
              placeholder="Search documents..." 
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ maxHeight: 300, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(doc => {
              const isSelected = selectedIds.includes(doc.id)
              return (
                <div 
                  key={doc.id}
                  onClick={() => onSelect(doc.id)}
                  style={{ 
                    padding: '10px 12px', 
                    borderRadius: 8, 
                    border: '1px solid var(--border)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: isSelected ? 'var(--accent-dim)' : 'transparent',
                    borderColor: isSelected ? 'var(--accent)' : 'var(--border)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileText size={14} color={isSelected ? 'var(--accent)' : 'var(--text-muted)'} />
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{doc.title}</span>
                  </div>
                  {isSelected && <CheckCircle size={14} color="var(--accent)" />}
                </div>
              )
            })}
            {filtered.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No documents found.</p>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary btn-block" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  )
}

function ClauseRow({ item, data, allDocs, onUpdate, onToggleDoc, onPickDocs, idx }) {
  const [localNotes, setLocalNotes] = useState(data.notes || '')
  const hasChanges = localNotes !== (data.notes || '')

  // Sync if data changes from outside (e.g. after save)
  useEffect(() => {
    setLocalNotes(data.notes || '')
  }, [data.notes])

  return (
    <div style={{
      padding: '14px 20px', borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
      display: 'flex', gap: 14, alignItems: 'flex-start'
    }}>
      <div style={{ marginTop: 2, flexShrink: 0 }}>{statusIcon(data.status)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--text-muted)', minWidth: 44, flexShrink: 0 }}>{item.id}</span>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>{item.text}</span>
        </div>
        
        <div style={{ paddingLeft: 54 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <select
              className="form-select"
              style={{ width: 180, padding: '5px 10px', fontSize: '0.8125rem' }}
              value={data.status}
              onChange={e => onUpdate(item.id, 'status', e.target.value)}
            >
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
            <div style={{ position: 'relative', flex: 1, display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                style={{ flex: 1, padding: '5px 10px', fontSize: '0.8125rem' }}
                placeholder="Evidence / notes…"
                value={localNotes}
                onChange={e => setLocalNotes(e.target.value)}
              />
              {hasChanges && (
                <button 
                  className="btn btn-primary btn-sm"
                  style={{ padding: '4px 10px' }}
                  onClick={() => onUpdate(item.id, 'notes', localNotes)}
                >
                  Save
                </button>
              )}
              <button 
                className="btn btn-secondary btn-sm" 
                style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => onPickDocs(item.id)}
              >
                <FilePlus size={14} /> Link Evidence
              </button>
            </div>
          </div>

          {/* Linked Docs */}
          {data.evidence_docs.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {data.evidence_docs.map(docId => {
                const doc = allDocs.find(d => d.id === docId)
                if (!doc) return null
                return (
                  <div key={docId} style={{ 
                    display: 'flex', alignItems: 'center', gap: 6, 
                    background: 'var(--bg-elevated)', padding: '4px 10px', 
                    borderRadius: 100, border: '1px solid var(--border)',
                    fontSize: '0.75rem', color: 'var(--text-secondary)'
                  }}>
                    <FileText size={12} color="var(--accent)" />
                    <span>{doc.title}</span>
                    <button 
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex' }}
                      onClick={() => onToggleDoc(item.id, docId)}
                    ><X size={12} /></button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
      <span className={`badge ${statusBadge(data.status)}`} style={{ flexShrink: 0, marginTop: 2 }}>
        {data.status}
      </span>
    </div>
  )
}

import { useEffect } from 'react'

export function Clauses({ toast }) {
  const qc = useQueryClient()
  const [collapsed, setCollapsed] = useState({})
  const [pickingFor, setPickingFor] = useState(null)

  const { data: clauseData = [], isLoading: loadingClauses } = useQuery({ queryKey: ['clauses'], queryFn: api.getClauses })
  const { data: allDocs = [] } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateClause(id, data),
    onSuccess: () => qc.invalidateQueries(['clauses']),
    onError: e => toast(e.message, 'error')
  })

  // Map backend results to a more usable object
  const clauseMap = useMemo(() => {
    const map = {}
    clauseData.forEach(c => {
      map[c.clause_id] = {
        status: c.status,
        notes: c.notes || '',
        evidence_docs: JSON.parse(c.evidence_docs || '[]')
      }
    })
    return map
  }, [clauseData])

  const allItems = CLAUSES.flatMap(c => c.items)
  const compliant = allItems.filter(i => (clauseMap[i.id]?.status === 'Compliant')).length
  const inProgress = allItems.filter(i => {
    const s = clauseMap[i.id]?.status
    return s === 'In Progress' || s === 'Partially Compliant'
  }).length
  const pct = allItems.length ? Math.round((compliant / allItems.length) * 100) : 0

  const handleUpdate = (id, field, value) => {
    const current = clauseMap[id] || { status: 'Not Started', notes: '', evidence_docs: [] }
    updateMut.mutate({ id, data: { ...current, [field]: value } })
  }

  const toggleDoc = (clauseId, docId) => {
    const current = clauseMap[clauseId] || { status: 'Not Started', notes: '', evidence_docs: [] }
    const docs = current.evidence_docs || []
    const nextDocs = docs.includes(docId) ? docs.filter(id => id !== docId) : [...docs, docId]
    handleUpdate(clauseId, 'evidence_docs', nextDocs)
  }

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Clause Compliance</h1>
          <p>ISO 27001:2022 — Clauses 4–10 conformance tracking</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Overall Compliance</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--accent)' }}>{pct}%</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24, padding: '16px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span className="text-sm text-muted">{compliant} of {allItems.length} requirements compliant</span>
          <span className="text-sm text-muted">{inProgress} in progress</span>
        </div>
        <div className="progress-bar" style={{ height: 10 }}>
          <div className="progress-fill" style={{ width: `${pct}%`, background: pct >= 80 ? 'var(--accent)' : pct >= 50 ? 'var(--warning)' : 'var(--danger)' }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {CLAUSES.map(clause => {
          const clauseItems = clause.items
          const clauseCompliant = clauseItems.filter(i => clauseMap[i.id]?.status === 'Compliant').length
          const isCollapsed = collapsed[clause.id]

          return (
            <div key={clause.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-elevated)' }}
                onClick={() => setCollapsed(p => ({ ...p, [clause.id]: !p[clause.id] }))}
              >
                {isCollapsed ? <ChevronRight size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem', color: 'var(--accent)', fontWeight: 700, minWidth: 28 }}>{clause.id}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{clause.title}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="progress-bar" style={{ width: 80 }}>
                    <div className="progress-fill" style={{ width: `${clauseItems.length ? (clauseCompliant / clauseItems.length * 100) : 0}%`, background: 'var(--accent)' }} />
                  </div>
                  <span className="text-xs text-muted">{clauseCompliant}/{clauseItems.length}</span>
                </div>
              </div>

              {!isCollapsed && (
                <div style={{ padding: '4px 0' }}>
                  {clauseItems.map((item, idx) => (
                    <ClauseRow 
                      key={item.id}
                      item={item}
                      idx={idx}
                      data={clauseMap[item.id] || { status: 'Not Started', notes: '', evidence_docs: [] }}
                      allDocs={allDocs}
                      onUpdate={handleUpdate}
                      onToggleDoc={toggleDoc}
                      onPickDocs={setPickingFor}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <DocPickerModal 
        isOpen={!!pickingFor}
        onClose={() => setPickingFor(null)}
        documents={allDocs}
        selectedIds={pickingFor ? (clauseMap[pickingFor]?.evidence_docs || []) : []}
        onSelect={(docId) => toggleDoc(pickingFor, docId)}
      />
    </div>
  )
}
